import type { AvatarStatus, DepositMethodsConfig } from '~/stores/user'

// Session ID storage key
const SESSION_ID_KEY = 'aurumrun_session_id'

// Response types
interface LoginResponse {
  success: boolean
  user: {
    id: string
    walletAddress: string | null
    email: string | null
    depositWalletAddress: string | null
    depositWalletSolanaAddress: string | null
    depositWalletTronAddress: string | null
    depositWalletTonAddress: string | null
    tonMemoCode: string | null
    avatarUrl?: string | null
    avatarStatus?: AvatarStatus | null
    avatarUpdatedAt?: string | null
    fiatDepositsEnabled?: boolean
    bitoloDepositsEnabled?: boolean
    speedDepositsEnabled?: boolean
    depositMethods?: DepositMethodsConfig
    isNewUser: boolean
    freeBetsGranted?: number[]
    visitorBalanceTransferred?: number
  }
  session: {
    sessionId?: string // For fast auth - store and send in X-Session-Id header
    maxConcurrent: number
    timeoutMinutes: number
    forceReauthHours: number
  }
}

interface LoginParams {
  accessToken: string
  referralCode?: string | null
  authMethod?: string | null
  visitorId?: string | null
  /**
   * Fingerprint Pro requestId — paired with visitorId for server-side
   * verification. Optional; backend treats absence as "unverified" and
   * falls back to trusting the client-supplied visitorId as a hint.
   */
  fingerprintRequestId?: string | null
  /**
   * Acquisition attribution (from the `attribution` cookie) — the LAST
   * marketing touch (last-touch, ref-aware), sent at signup so we know where
   * every user came from — organic included.
   */
  attribution?: {
    utm_source?: string | null
    utm_medium?: string | null
    utm_campaign?: string | null
    utm_term?: string | null
    utm_content?: string | null
    /** Extra ad-network click slots (?param1..3), echoed back in postbacks. */
    param1?: string | null
    param2?: string | null
    param3?: string | null
    landing_page?: string | null
    http_referrer?: string | null
  } | null
}

/**
 * Get the stored session ID for fast auth
 */
export function getSessionId(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(SESSION_ID_KEY)
}

/**
 * Store session ID for fast auth
 */
export function setSessionId(sessionId: string): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(SESSION_ID_KEY, sessionId)
}

/**
 * Clear session ID (on logout)
 */
export function clearSessionId(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(SESSION_ID_KEY)
}

/**
 * Authenticate with backend using Privy access token.
 * POST /auth-login
 *
 * Stores the sessionId from response for fast auth on subsequent requests.
 */
export async function login(params: LoginParams): Promise<LoginResponse> {
  const config = useRuntimeConfig()

  const response = await $fetch<LoginResponse>(`${config.public.apiBase}/auth-login`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${params.accessToken}`,
      ...(config.public.supabaseRegion ? { 'x-region': String(config.public.supabaseRegion) } : {}),
    },
    body: {
      referralCode: params.referralCode || undefined,
      authMethod: params.authMethod || undefined,
      visitorId: params.visitorId || undefined,
      fingerprintRequestId: params.fingerprintRequestId || undefined,
      // Last-touch acquisition attribution (server derives acquisition_channel).
      utmSource: params.attribution?.utm_source || undefined,
      utmMedium: params.attribution?.utm_medium || undefined,
      utmCampaign: params.attribution?.utm_campaign || undefined,
      utmTerm: params.attribution?.utm_term || undefined,
      utmContent: params.attribution?.utm_content || undefined,
      param1: params.attribution?.param1 || undefined,
      param2: params.attribution?.param2 || undefined,
      param3: params.attribution?.param3 || undefined,
      landingPage: params.attribution?.landing_page || undefined,
      httpReferrer: params.attribution?.http_referrer || undefined,
    },
  })

  // Store session ID for fast auth
  if (response.session?.sessionId) {
    setSessionId(response.session.sessionId)
  }

  return response
}

/**
 * Logout from backend.
 * POST /auth-logout
 *
 * Also clears the stored session ID.
 */
export async function logout(accessToken: string): Promise<void> {
  const config = useRuntimeConfig()

  // Read session ID before clearing it
  const sessionId = getSessionId()

  // Clear session ID (even if API call fails)
  clearSessionId()

  await $fetch(`${config.public.apiBase}/auth-logout`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(sessionId && { 'X-Session-Id': sessionId }),
    },
  })
}

// Re-export types
export type { LoginResponse, LoginParams }
